import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import api, { resolveMediaUrl, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import {
    Plus, Trash2, Pencil, LogOut, Upload, X, Inbox, Car as CarIcon, Activity, Star, KeyRound,
} from "lucide-react";

const EMPTY = {
    make: "", model: "", year: new Date().getFullYear(),
    mileage: "", fuel: "Diesel", transmission: "Manual",
    body_type: "", engine_size: "", color: "", vin: "",
    condition: "Dismantling", description: "", image_urls: [], featured: false,
};

export default function AdminDashboard() {
    const { user, logout, loading } = useAuth();
    const navigate = useNavigate();
    const [tab, setTab] = useState("stock");
    const [cars, setCars] = useState([]);
    const [messages, setMessages] = useState([]);
    const [activity, setActivity] = useState([]);
    const [editing, setEditing] = useState(null); // null | "new" | car object
    const [form, setForm] = useState(EMPTY);
    const [uploading, setUploading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [pwdOpen, setPwdOpen] = useState(false);
    const [pwdForm, setPwdForm] = useState({ current_password: "", new_password: "", confirm: "" });
    const [pwdSaving, setPwdSaving] = useState(false);
    const prevUnreadRef = useRef(0);

    // Poll for new messages every 30 seconds while dashboard is open.
    // When unread count grows we play a soft chime + show a toast so admin
    // gets an in-app notification even without email delivery.
    useEffect(() => {
        if (!user?.email) return;
        const iv = setInterval(() => {
            loadMessages(true);
        }, 30000);
        return () => clearInterval(iv);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user?.email]);

    const chime = () => {
        try {
            const Ctx = window.AudioContext || window.webkitAudioContext;
            if (!Ctx) return;
            const ctx = new Ctx();
            const o = ctx.createOscillator();
            const g = ctx.createGain();
            o.connect(g);
            g.connect(ctx.destination);
            o.type = "sine";
            o.frequency.setValueAtTime(660, ctx.currentTime);
            o.frequency.exponentialRampToValueAtTime(880, ctx.currentTime + 0.15);
            g.gain.setValueAtTime(0.001, ctx.currentTime);
            g.gain.exponentialRampToValueAtTime(0.25, ctx.currentTime + 0.02);
            g.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.4);
            o.start();
            o.stop(ctx.currentTime + 0.45);
        } catch { /* audio blocked — fine, toast still fires */ }
    };

    const submitPasswordChange = async (e) => {
        e.preventDefault();
        if (pwdForm.new_password !== pwdForm.confirm) {
            toast.error("New passwords don't match");
            return;
        }
        if (pwdForm.new_password.length < 8) {
            toast.error("New password must be at least 8 characters");
            return;
        }
        setPwdSaving(true);
        try {
            await api.post("/auth/change-password", {
                current_password: pwdForm.current_password,
                new_password: pwdForm.new_password,
            });
            toast.success("Password changed. Please log in again.");
            setPwdOpen(false);
            setPwdForm({ current_password: "", new_password: "", confirm: "" });
            await logout();
            navigate("/admin/login");
        } catch (err) {
            toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Password change failed");
        } finally {
            setPwdSaving(false);
        }
    };

    useEffect(() => {
        if (!loading && (!user || !user.email)) navigate("/admin/login", { replace: true });
    }, [user, loading, navigate]);
    const loadCars = async () => {
        const r = await api.get("/cars");
        setCars(r.data || []);
    };
    const loadMessages = async (isPoll = false) => {
        try {
            const r = await api.get("/admin/messages");
            const list = r.data || [];
            const unread = list.filter((m) => !m.read).length;
            if (isPoll && unread > prevUnreadRef.current && prevUnreadRef.current !== 0) {
                const diff = unread - prevUnreadRef.current;
                chime();
                toast.success(`${diff} new message${diff > 1 ? "s" : ""} — check Inbox`, { duration: 6000 });
                document.title = `(${unread}) Auto Europarts · Admin`;
            } else if (isPoll && unread === 0) {
                document.title = "Auto Europarts · Admin";
            }
            prevUnreadRef.current = unread;
            setMessages(list);
        } catch {
            // ignore transient errors
        }
    };
    const loadActivity = async () => {
        try {
            const r = await api.get("/admin/activity-log");
            setActivity(r.data || []);
        } catch {
            // ignore
        }
    };
    useEffect(() => {
        if (user && user.email) {
            loadCars();
            loadMessages();
            loadActivity();
        }
    }, [user]);

    const openNew = () => {
        setForm(EMPTY);
        setEditing("new");
    };
    const openEdit = (car) => {
        setForm({ ...EMPTY, ...car });
        setEditing(car);
    };

    const update = (k) => (e) =>
        setForm({ ...form, [k]: e.target?.value !== undefined ? e.target.value : e });

    const onUpload = async (e) => {
        const files = Array.from(e.target.files || []);
        if (files.length === 0) return;
        setUploading(true);
        try {
            const urls = [...form.image_urls];
            for (const f of files) {
                const fd = new FormData();
                fd.append("file", f);
                const r = await api.post("/admin/uploads", fd, {
                    headers: { "Content-Type": "multipart/form-data" },
                });
                urls.push(r.data.url);
            }
            setForm({ ...form, image_urls: urls });
            toast.success(`${files.length} image(s) uploaded`);
        } catch (err) {
            toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Upload failed");
        } finally {
            setUploading(false);
            e.target.value = "";
        }
    };

    const removeImage = (i) => {
        setForm({ ...form, image_urls: form.image_urls.filter((_, idx) => idx !== i) });
    };

    const save = async () => {
        if (!form.make || !form.model || !form.year) {
            toast.error("Make, model and year are required");
            return;
        }
        setSaving(true);
        const payload = {
            ...form,
            year: parseInt(form.year, 10),
            mileage: form.mileage ? parseInt(form.mileage, 10) : null,
        };
        try {
            if (editing === "new") {
                await api.post("/admin/cars", payload);
                toast.success("Car added");
            } else {
                await api.put(`/admin/cars/${editing.id}`, payload);
                toast.success("Car updated");
            }
            setEditing(null);
            loadCars();
        } catch (err) {
            toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Save failed");
        } finally {
            setSaving(false);
        }
    };

    const removeCar = async (car) => {
        if (!window.confirm(`Delete ${car.make} ${car.model}?`)) return;
        try {
            await api.delete(`/admin/cars/${car.id}`);
            toast.success("Car deleted");
            loadCars();
        } catch (err) {
            toast.error(formatApiErrorDetail(err?.response?.data?.detail));
        }
    };

    const toggleFeatured = async (car) => {
        try {
            const { data } = await api.put(`/admin/cars/${car.id}/featured`);
            toast.success(data.featured ? "Pinned as Featured" : "Removed from Featured");
            loadCars();
        } catch (err) {
            toast.error(formatApiErrorDetail(err?.response?.data?.detail));
        }
    };

    const removeMessage = async (m) => {
        try {
            await api.delete(`/admin/messages/${m.id}`);
            loadMessages();
        } catch (err) {
            toast.error(formatApiErrorDetail(err?.response?.data?.detail));
        }
    };

    const markMessageRead = async (m) => {
        try {
            await api.patch(`/admin/messages/${m.id}/read`);
            setMessages((prev) => prev.map((x) => (x.id === m.id ? { ...x, read: true } : x)));
        } catch (err) {
            toast.error(formatApiErrorDetail(err?.response?.data?.detail));
        }
    };

    const markAllRead = async () => {
        try {
            const { data } = await api.post("/admin/messages/mark-all-read");
            setMessages((prev) => prev.map((x) => ({ ...x, read: true })));
            toast.success(`${data.modified || 0} message${data.modified === 1 ? "" : "s"} marked as read`);
        } catch (err) {
            toast.error(formatApiErrorDetail(err?.response?.data?.detail));
        }
    };

    if (loading || !user || !user.email) {
        return <div className="min-h-screen bg-[#0a0a0a] text-white p-10">Loading...</div>;
    }

    return (
        <div className="min-h-screen bg-[#0a0a0a] text-white" data-testid="admin-dashboard">
            <header className="border-b border-white/10 bg-[#0a0a0a]/90 backdrop-blur-xl sticky top-0 z-40">
                <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
                    <div className="flex items-center gap-6">
                        <div className="font-display uppercase text-xl tracking-tight">
                            Admin <span className="text-[#d52b1e]">Console</span>
                        </div>
                        <nav className="flex items-center gap-6">
                            <TabBtn active={tab === "stock"} onClick={() => setTab("stock")} testid="tab-stock">
                                <CarIcon className="w-3.5 h-3.5" /> Stock ({cars.length})
                            </TabBtn>
                            <TabBtn active={tab === "messages"} onClick={() => setTab("messages")} testid="tab-messages">
                                <Inbox className="w-3.5 h-3.5" /> Inbox ({messages.length})
                                {messages.filter((m) => !m.read).length > 0 && (
                                    <span
                                        data-testid="unread-badge"
                                        className="ml-1 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 text-[10px] font-bold bg-[#d52b1e] text-white rounded-full animate-pulse"
                                    >
                                        {messages.filter((m) => !m.read).length}
                                    </span>
                                )}
                            </TabBtn>
                            <TabBtn active={tab === "activity"} onClick={() => { setTab("activity"); loadActivity(); }} testid="tab-activity">
                                <Activity className="w-3.5 h-3.5" /> Activity
                            </TabBtn>
                        </nav>
                    </div>
                    <div className="flex items-center gap-4">
                        <span className="text-xs text-zinc-400 hidden sm:inline">{user.email}</span>
                        <button
                            onClick={() => setPwdOpen(true)}
                            className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-zinc-300 hover:text-white"
                            data-testid="admin-change-password-btn"
                            title="Change your password"
                        >
                            <KeyRound className="w-3.5 h-3.5" /> Password
                        </button>
                        <button
                            onClick={async () => {
                                await logout();
                                navigate("/admin/login");
                            }}
                            className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-zinc-300 hover:text-white"
                            data-testid="admin-logout-btn"
                        >
                            <LogOut className="w-3.5 h-3.5" /> Logout
                        </button>
                    </div>
                </div>
            </header>

            <main className="max-w-7xl mx-auto px-6 py-10">
                {tab === "stock" && (
                    <>
                        <div className="flex items-center justify-between mb-8">
                            <div>
                                <div className="text-[10px] uppercase tracking-[0.3em] text-[#d52b1e] mb-1">
                                    Inventory
                                </div>
                                <h1 className="font-display uppercase text-4xl">Stock cars</h1>
                            </div>
                            <button
                                onClick={openNew}
                                className="bg-[#d52b1e] hover:bg-[#f03022] text-white px-5 py-3 uppercase text-xs tracking-[0.25em] font-bold inline-flex items-center gap-2"
                                data-testid="admin-add-car-btn"
                            >
                                <Plus className="w-4 h-4" /> Add car
                            </button>
                        </div>
                        <div className="bg-[#18181a] border border-white/10 overflow-x-auto">
                            <table className="w-full text-sm">
                                <thead className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 border-b border-white/10">
                                    <tr>
                                        <th className="text-left p-4">Vehicle</th>
                                        <th className="text-left p-4">Year</th>
                                        <th className="text-left p-4">Fuel</th>
                                        <th className="text-left p-4">Mileage</th>
                                        <th className="text-left p-4">Status</th>
                                        <th className="text-right p-4">Actions</th>
                                    </tr>
                                </thead>
                                <tbody data-testid="admin-cars-table">
                                    {cars.map((c) => (
                                        <tr key={c.id} className="border-b border-white/5 hover:bg-white/[0.02]">
                                            <td className="p-4 flex items-center gap-3">
                                                <div className="w-14 h-10 bg-black overflow-hidden">
                                                    {c.image_urls?.[0] && (
                                                        <img
                                                            src={resolveMediaUrl(c.image_urls[0])}
                                                            alt=""
                                                            className="w-full h-full object-cover"
                                                        />
                                                    )}
                                                </div>
                                                <div>
                                                    <div className="font-display uppercase text-base leading-tight">
                                                        {c.make} {c.model}
                                                    </div>
                                                    <div className="text-[10px] text-zinc-500 uppercase tracking-[0.2em]">
                                                        {c.body_type || ""}
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="p-4">{c.year}</td>
                                            <td className="p-4">{c.fuel}</td>
                                            <td className="p-4">
                                                {c.mileage ? `${c.mileage.toLocaleString()} km` : "—"}
                                            </td>
                                            <td className="p-4">
                                                <span
                                                    className={`text-[10px] uppercase tracking-[0.2em] px-2 py-1 ${
                                                        c.condition === "Sold"
                                                            ? "bg-zinc-700 text-zinc-300"
                                                            : c.condition === "Available"
                                                              ? "bg-emerald-700 text-white"
                                                              : "bg-[#d52b1e] text-white"
                                                    }`}
                                                >
                                                    {c.condition}
                                                </span>
                                            </td>
                                            <td className="p-4 text-right">
                                                <button
                                                    onClick={() => toggleFeatured(c)}
                                                    className={`p-1.5 transition-colors ${
                                                        c.featured
                                                            ? "text-amber-400 hover:text-amber-300"
                                                            : "text-zinc-500 hover:text-amber-400"
                                                    }`}
                                                    title={c.featured ? "Unpin from Featured" : "Pin as Featured"}
                                                    data-testid={`feature-toggle-${c.id}`}
                                                >
                                                    <Star className={`w-4 h-4 ${c.featured ? "fill-amber-400" : ""}`} />
                                                </button>
                                                <button
                                                    onClick={() => openEdit(c)}
                                                    className="text-zinc-300 hover:text-white p-1.5 ml-2"
                                                    data-testid={`edit-car-${c.id}`}
                                                >
                                                    <Pencil className="w-4 h-4" />
                                                </button>
                                                <button
                                                    onClick={() => removeCar(c)}
                                                    className="text-[#d52b1e] hover:text-[#f03022] p-1.5 ml-2"
                                                    data-testid={`delete-car-${c.id}`}
                                                >
                                                    <Trash2 className="w-4 h-4" />
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                    {cars.length === 0 && (
                                        <tr>
                                            <td colSpan={6} className="p-10 text-center text-zinc-500">
                                                No cars yet. Click "Add car" to start.
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </>
                )}

                {tab === "messages" && (
                    <>
                        <div className="mb-8 flex items-start justify-between gap-4">
                            <div>
                                <div className="text-[10px] uppercase tracking-[0.3em] text-[#d52b1e] mb-1">
                                    Customer Inbox
                                </div>
                                <h1 className="font-display uppercase text-4xl">Messages</h1>
                            </div>
                            {messages.some((m) => !m.read) && (
                                <button
                                    onClick={markAllRead}
                                    className="text-[10px] uppercase tracking-[0.3em] border border-white/20 hover:border-white px-4 py-2 mt-6"
                                    data-testid="mark-all-read-btn"
                                >
                                    Mark all read
                                </button>
                            )}
                        </div>
                        <div className="grid gap-4">
                            {messages.length === 0 && (
                                <div className="bg-[#18181a] border border-white/10 p-10 text-center text-zinc-500">
                                    No messages yet.
                                </div>
                            )}
                            {messages.map((m) => (
                                <div
                                    key={m.id}
                                    className={`border p-5 transition-colors ${
                                        m.read
                                            ? "bg-[#18181a] border-white/10"
                                            : "bg-[#1a1112] border-[#d52b1e]/40"
                                    }`}
                                    data-testid={`message-${m.id}`}
                                >
                                    <div className="flex items-start justify-between gap-4">
                                        <div className="flex items-start gap-3">
                                            {!m.read && (
                                                <span className="mt-1.5 w-2 h-2 rounded-full bg-[#d52b1e] flex-shrink-0" title="Unread" />
                                            )}
                                            <div>
                                                <div className="font-display uppercase text-lg">
                                                    {m.name}
                                                </div>
                                                <div className="text-xs text-zinc-400">
                                                    {m.email} {m.phone ? `· ${m.phone}` : ""}
                                                </div>
                                                {m.subject && (
                                                    <div className="text-xs text-zinc-300 mt-1">
                                                        Subject: <span className="text-white">{m.subject}</span>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-2">
                                            {!m.read && (
                                                <button
                                                    onClick={() => markMessageRead(m)}
                                                    className="text-[10px] uppercase tracking-[0.25em] text-zinc-400 hover:text-white"
                                                    data-testid={`mark-read-${m.id}`}
                                                >
                                                    Mark read
                                                </button>
                                            )}
                                            <button
                                                onClick={() => removeMessage(m)}
                                                className="text-zinc-500 hover:text-[#d52b1e]"
                                                data-testid={`delete-message-${m.id}`}
                                            >
                                                <Trash2 className="w-4 h-4" />
                                            </button>
                                        </div>
                                    </div>
                                    <p className="mt-3 text-sm text-zinc-300 whitespace-pre-line">
                                        {m.message}
                                    </p>
                                    <div className="mt-3 text-[10px] uppercase tracking-[0.25em] text-zinc-600">
                                        {new Date(m.created_at).toLocaleString()}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </>
                )}

                {tab === "activity" && (
                    <>
                        <div className="mb-8">
                            <div className="text-[10px] uppercase tracking-[0.3em] text-[#d52b1e] mb-1">
                                Audit Trail
                            </div>
                            <h1 className="font-display uppercase text-4xl">Activity log</h1>
                        </div>
                        <div className="bg-[#18181a] border border-white/10 overflow-x-auto">
                            <table className="w-full text-sm">
                                <thead className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 border-b border-white/10">
                                    <tr>
                                        <th className="text-left p-4">When</th>
                                        <th className="text-left p-4">Action</th>
                                        <th className="text-left p-4">Actor</th>
                                        <th className="text-left p-4">Details</th>
                                    </tr>
                                </thead>
                                <tbody data-testid="admin-activity-table">
                                    {activity.length === 0 && (
                                        <tr>
                                            <td colSpan={4} className="p-10 text-center text-zinc-500">No activity recorded yet.</td>
                                        </tr>
                                    )}
                                    {activity.map((row) => (
                                        <tr key={row.id} className="border-b border-white/5 hover:bg-white/[0.02]">
                                            <td className="p-4 text-zinc-400 text-xs whitespace-nowrap">
                                                {new Date(row.ts).toLocaleString()}
                                            </td>
                                            <td className="p-4">
                                                <span className={`inline-block text-[10px] uppercase tracking-[0.2em] px-2 py-1 ${
                                                    row.action.startsWith("login_success") || row.action === "2fa_enabled" ? "bg-emerald-700 text-white"
                                                    : row.action.includes("failed") || row.action.includes("denied") ? "bg-[#d52b1e] text-white"
                                                    : "bg-zinc-700 text-zinc-200"
                                                }`}>
                                                    {row.action}
                                                </span>
                                            </td>
                                            <td className="p-4 text-xs text-zinc-300">{row.actor_email || "—"}</td>
                                            <td className="p-4 text-xs text-zinc-400 font-mono break-all">
                                                {row.meta && Object.keys(row.meta).length > 0 ? JSON.stringify(row.meta) : ""}
                                                {row.ip && <span className="ml-2 text-zinc-600">{row.ip}</span>}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </>
                )}

            </main>

            {/* Edit/Create Drawer */}
            {editing && (
                <div className="fixed inset-0 z-50 bg-black/70 flex items-stretch justify-end">
                    <div className="bg-[#0a0a0a] border-l border-white/10 w-full max-w-2xl overflow-y-auto" data-testid="car-edit-drawer">
                        <div className="sticky top-0 bg-[#0a0a0a] border-b border-white/10 px-6 py-4 flex items-center justify-between">
                            <div>
                                <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500">
                                    {editing === "new" ? "New vehicle" : "Edit vehicle"}
                                </div>
                                <div className="font-display uppercase text-2xl">
                                    {form.make || "—"} {form.model || ""}
                                </div>
                            </div>
                            <button
                                onClick={() => setEditing(null)}
                                className="text-zinc-400 hover:text-white"
                                data-testid="drawer-close"
                            >
                                <X />
                            </button>
                        </div>
                        <div className="p-6 grid grid-cols-2 gap-x-6 gap-y-6">
                            <Field label="Make"><input className="industrial-input" value={form.make} onChange={update("make")} data-testid="form-make" /></Field>
                            <Field label="Model"><input className="industrial-input" value={form.model} onChange={update("model")} data-testid="form-model" /></Field>
                            <Field label="Year"><input type="number" className="industrial-input" value={form.year} onChange={update("year")} data-testid="form-year" /></Field>
                            <Field label="Mileage (km)"><input type="number" className="industrial-input" value={form.mileage} onChange={update("mileage")} data-testid="form-mileage" /></Field>
                            <Field label="Fuel">
                                <select className="industrial-input" value={form.fuel} onChange={update("fuel")} data-testid="form-fuel">
                                    {["Petrol", "Diesel", "Hybrid", "Electric", "LPG"].map((o) => (
                                        <option key={o} value={o} className="bg-[#0a0a0a]">{o}</option>
                                    ))}
                                </select>
                            </Field>
                            <Field label="Transmission">
                                <select className="industrial-input" value={form.transmission} onChange={update("transmission")} data-testid="form-transmission">
                                    {["Manual", "Automatic"].map((o) => (
                                        <option key={o} value={o} className="bg-[#0a0a0a]">{o}</option>
                                    ))}
                                </select>
                            </Field>
                            <Field label="Body type"><input className="industrial-input" value={form.body_type} onChange={update("body_type")} /></Field>
                            <Field label="Engine size (L)"><input className="industrial-input" value={form.engine_size} onChange={update("engine_size")} /></Field>
                            <Field label="Colour"><input className="industrial-input" value={form.color} onChange={update("color")} /></Field>
                            <Field label="VIN"><input className="industrial-input" value={form.vin} onChange={update("vin")} /></Field>
                            <Field label="Status">
                                <select className="industrial-input" value={form.condition} onChange={update("condition")} data-testid="form-condition">
                                    {["Dismantling", "Available", "Sold"].map((o) => (
                                        <option key={o} value={o} className="bg-[#0a0a0a]">{o}</option>
                                    ))}
                                </select>
                            </Field>
                            <Field label="Featured">
                                <button
                                    type="button"
                                    onClick={() => setForm({ ...form, featured: !form.featured })}
                                    data-testid="form-featured-toggle"
                                    className={`industrial-input flex items-center justify-between w-full text-left ${
                                        form.featured ? "text-amber-400" : "text-zinc-400"
                                    }`}
                                >
                                    <span className="inline-flex items-center gap-2">
                                        <Star className={`w-4 h-4 ${form.featured ? "fill-amber-400" : ""}`} />
                                        {form.featured ? "Pinned as Featured" : "Not Featured"}
                                    </span>
                                    <span className="text-[10px] uppercase tracking-[0.25em]">
                                        {form.featured ? "On" : "Off"}
                                    </span>
                                </button>
                            </Field>
                            <div className="col-span-2">
                                <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500 mb-2">
                                    Description / Parts available
                                </div>
                                <textarea
                                    rows={4}
                                    className="industrial-input resize-none"
                                    value={form.description}
                                    onChange={update("description")}
                                    data-testid="form-description"
                                />
                            </div>
                            <div className="col-span-2">
                                <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500 mb-3">
                                    Photos
                                </div>
                                <div className="grid grid-cols-4 gap-3 mb-3">
                                    {form.image_urls.map((url, i) => (
                                        <div key={i} className="relative aspect-[4/3] bg-black border border-white/10">
                                            <img src={resolveMediaUrl(url)} alt="" className="w-full h-full object-cover" />
                                            <button
                                                onClick={() => removeImage(i)}
                                                className="absolute top-1 right-1 bg-black/70 text-white p-1"
                                                data-testid={`remove-image-${i}`}
                                            >
                                                <X className="w-3 h-3" />
                                            </button>
                                        </div>
                                    ))}
                                    <label className="aspect-[4/3] border border-dashed border-white/20 hover:border-white/40 flex flex-col items-center justify-center cursor-pointer text-zinc-400 text-xs" data-testid="form-upload-label">
                                        <Upload className="w-4 h-4 mb-1" />
                                        {uploading ? "Uploading..." : "Upload"}
                                        <input
                                            type="file"
                                            accept="image/*"
                                            multiple
                                            className="hidden"
                                            onChange={onUpload}
                                            disabled={uploading}
                                            data-testid="form-upload-input"
                                        />
                                    </label>
                                </div>
                            </div>
                        </div>
                        <div className="sticky bottom-0 bg-[#0a0a0a] border-t border-white/10 px-6 py-4 flex justify-end gap-3">
                            <button
                                onClick={() => setEditing(null)}
                                className="border border-white/20 text-white px-5 py-3 uppercase text-xs tracking-[0.25em] font-bold"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={save}
                                disabled={saving}
                                className="bg-[#d52b1e] hover:bg-[#f03022] disabled:opacity-60 text-white px-5 py-3 uppercase text-xs tracking-[0.25em] font-bold"
                                data-testid="form-save-btn"
                            >
                                {saving ? "Saving..." : "Save"}
                            </button>
                        </div>
                    </div>
                </div>
            )}
            {pwdOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4">
                    <form
                        onSubmit={submitPasswordChange}
                        className="w-full max-w-md bg-[#0a0a0a] border border-white/10 p-6 space-y-4"
                        data-testid="change-password-modal"
                    >
                        <div className="flex items-center justify-between">
                            <div>
                                <div className="text-[10px] uppercase tracking-[0.3em] text-[#d52b1e]">Security</div>
                                <h2 className="font-display uppercase text-2xl">Change Password</h2>
                            </div>
                            <button
                                type="button"
                                onClick={() => setPwdOpen(false)}
                                className="text-zinc-400 hover:text-white"
                            >
                                <X className="w-5 h-5" />
                            </button>
                        </div>
                        <Field label="Current password">
                            <input
                                type="password"
                                className="industrial-input"
                                autoComplete="current-password"
                                required
                                value={pwdForm.current_password}
                                onChange={(e) => setPwdForm({ ...pwdForm, current_password: e.target.value })}
                                data-testid="pwd-current-input"
                            />
                        </Field>
                        <Field label="New password (min. 8 characters)">
                            <input
                                type="password"
                                className="industrial-input"
                                autoComplete="new-password"
                                required
                                minLength={8}
                                value={pwdForm.new_password}
                                onChange={(e) => setPwdForm({ ...pwdForm, new_password: e.target.value })}
                                data-testid="pwd-new-input"
                            />
                        </Field>
                        <Field label="Confirm new password">
                            <input
                                type="password"
                                className="industrial-input"
                                autoComplete="new-password"
                                required
                                value={pwdForm.confirm}
                                onChange={(e) => setPwdForm({ ...pwdForm, confirm: e.target.value })}
                                data-testid="pwd-confirm-input"
                            />
                        </Field>
                        <div className="flex justify-end gap-3 pt-2">
                            <button
                                type="button"
                                onClick={() => setPwdOpen(false)}
                                className="border border-white/20 text-white px-5 py-3 uppercase text-xs tracking-[0.25em] font-bold"
                            >
                                Cancel
                            </button>
                            <button
                                type="submit"
                                disabled={pwdSaving}
                                className="bg-[#d52b1e] hover:bg-[#f03022] disabled:opacity-60 text-white px-5 py-3 uppercase text-xs tracking-[0.25em] font-bold"
                                data-testid="pwd-save-btn"
                            >
                                {pwdSaving ? "Changing..." : "Change Password"}
                            </button>
                        </div>
                    </form>
                </div>
            )}
        </div>
    );
}

function Field({ label, children }) {
    return (
        <label className="block">
            <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500 mb-1">
                {label}
            </div>
            {children}
        </label>
    );
}

function TabBtn({ active, onClick, children, testid }) {
    return (
        <button
            onClick={onClick}
            data-testid={testid}
            className={`inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] pb-1 border-b-2 transition-colors ${active ? "border-[#d52b1e] text-white" : "border-transparent text-zinc-400 hover:text-white"}`}
        >
            {children}
        </button>
    );
}
